/**
 * App lifecycle panel — deploy backend/frontend and list build sessions.
 */

import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  CheckCircle2,
  Layers,
  Loader2,
  Rocket,
} from 'lucide-react';
import { callMcpTool } from '@/lib/mcpToolClient';
import {
  deployAppBackend,
  deployAppBuild,
  listBuildSessions,
} from '../api';

export interface AppLifecyclePanelProps {
  appId: string;
  appName?: string;
  tenantId?: string;
  mcpKey: string | null;
  settings: any;
  onRefresh: () => void;
}

export function AppLifecyclePanel({
  appId,
  tenantId = 'dev',
  mcpKey,
  settings,
  onRefresh,
}: AppLifecyclePanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const contract = settings?.settings?.contract ?? null;
  const validationReport = settings?.settings?.validation_report ?? null;

  const sessionsQuery = useQuery({
    queryKey: ['build-sessions', appId, tenantId],
    queryFn: () => listBuildSessions(appId, tenantId, { limit: 5 }),
    enabled: Boolean(appId),
    retry: false,
  });

  const deployBackendMutation = useMutation({
    mutationFn: () => deployAppBackend(appId),
    onSuccess: (data) => {
      setActionResult(JSON.stringify(data, null, 2));
      onRefresh();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const deployBuildMutation = useMutation({
    mutationFn: (file: File) => deployAppBuild(appId, file),
    onSuccess: (data) => {
      setActionResult(JSON.stringify(data, null, 2));
      onRefresh();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  async function runMcpTool(toolName: string, args: Record<string, unknown>) {
    if (!mcpKey) {
      setActionError('Provision an MCP key in the MCP Connection card above.');
      return;
    }
    setActionError(null);
    setActionResult(null);
    try {
      const { data } = await callMcpTool(appId, mcpKey, toolName, args);
      setActionResult(JSON.stringify(data, null, 2));
      onRefresh();
    } catch (e: any) {
      setActionError(e?.message || String(e));
    }
  }

  const busy = deployBackendMutation.isPending || deployBuildMutation.isPending;

  return (
    <Card className="border-indigo-200">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">App lifecycle</h3>
          {validationReport?.status && (
            <Badge variant={validationReport.status === 'pass' ? 'default' : 'destructive'} className="text-[10px]">
              {validationReport.status}
            </Badge>
          )}
        </div>

        {!mcpKey && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            MCP-gated steps require an app MCP key — use the MCP Connection card above to provision or rotate.
          </p>
        )}

        <div className="space-y-2 border-t pt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deploy</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => deployBackendMutation.mutate()}
            >
              {deployBackendMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Rocket className="w-3.5 h-3.5 mr-1" />
              )}
              Deploy backend
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileInputRef.current?.click()}>
              Upload frontend zip
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) deployBuildMutation.mutate(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="space-y-2 border-t pt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Build sessions</p>
          {sessionsQuery.isLoading && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading sessions…
            </p>
          )}
          {sessionsQuery.isError && (
            <p className="text-xs text-gray-500">Build sessions unavailable on this environment.</p>
          )}
          {(sessionsQuery.data?.sessions ?? []).slice(0, 3).map((s: any) => (
            <div key={s.session_id || s.id} className="text-xs text-gray-600 border rounded px-2 py-1">
              {s.session_id || s.id} · {s.tool_calls ?? s.event_count ?? '?'} calls
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            disabled={!mcpKey}
            onClick={() => runMcpTool('dc__get_build_patterns', { min_confidence: 0.1 })}
          >
            Load build patterns
          </Button>
        </div>

        {actionError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex gap-1">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {actionError}
          </div>
        )}
        {actionResult && (
          <pre className="text-[10px] bg-green-50 border border-green-200 rounded p-2 max-h-32 overflow-auto">
            {actionResult}
          </pre>
        )}
        {contract && !actionError && !actionResult && (
          <p className="text-[10px] text-gray-400 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-green-600" />
            Contract persisted for this app
          </p>
        )}
      </CardContent>
    </Card>
  );
}
